import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, Shield, CalendarDays, Home, User2, Menu, Table2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import logoImage from "@/assets/DISLOGIQ - LOGO.svg";

/**
 * Barre de navigation avec onglet actif (NavLink)
 * - Surbrillance automatique du lien actif
 * - Lien Admin visible seulement si role === "Admin"
 * - Bouton Déconnexion
 * - Menu Burger en mode mobile
 */
export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  const pillBase =
    "inline-flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-200 text-sm relative";
  const pillInactive =
    "bg-white/0 border-transparent hover:bg-muted/50 text-foreground hover:scale-105";
  const pillActive =
    "bg-primary text-primary-foreground border-primary shadow-md scale-105";

  const mobileLinkClass = "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors hover:bg-accent";

  return (
    <header className="sticky top-0 z-30 w-full border-b bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80 shadow-sm transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
        {/* Logo / titre */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <img 
              src={logoImage} 
              alt="DISLOGIQ" 
              className="h-5 w-auto sm:h-6"
            />
          </div>
        </div>

        {/* Navigation principale - Desktop */}
        <nav className="hidden md:flex items-center gap-2">
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              cn(pillBase, isActive ? pillActive : pillInactive)
            }
          >
            <Home className="h-4 w-4" />
            Rapports
          </NavLink>

          {(user?.role === "Admin" || user?.role === "Manager") && (
            <NavLink
              to="/schedules"
              className={({ isActive }) =>
                cn(pillBase, isActive ? pillActive : pillInactive)
              }
            >
              <CalendarDays className="h-4 w-4" />
              Planification
            </NavLink>
          )}

          {(user?.role === "Admin" || user?.role === "Manager") && (
            <NavLink
              to="/data-explorer"
              className={({ isActive }) =>
                cn(pillBase, isActive ? pillActive : pillInactive)
              }
            >
              <Table2 className="h-4 w-4" />
              Data Explorer
            </NavLink>
          )}

          {user?.role === "Admin" && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                cn(pillBase, isActive ? pillActive : pillInactive)
              }
            >
              <Shield className="h-4 w-4" />
              Admin
            </NavLink>
          )}
        </nav>

        {/* Profil + actions */}
        <div className="flex items-center gap-3">
          {user && (
            <div className="hidden sm:flex items-center gap-2 text-muted-foreground">
              <User2 className="h-4 w-4" />
              <span className="text-sm">{user.email}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-foreground">
                {user.role}
              </span>
            </div>
          )}
          <Button variant="outline" className="gap-2 hover:bg-destructive/10 hover:text-destructive hover:border-destructive transition-all duration-200" onClick={handleLogout}>
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Déconnexion</span>
          </Button>

          {/* Menu Burger - Mobile */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] sm:w-[400px]">
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-2 mt-6">
                <NavLink
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(mobileLinkClass, isActive && "bg-accent")
                  }
                >
                  <Home className="h-5 w-5" />
                  Rapports
                </NavLink>

                {(user?.role === "Admin" || user?.role === "Manager") && (
                  <NavLink
                    to="/schedules"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      cn(mobileLinkClass, isActive && "bg-accent")
                    }
                  >
                    <CalendarDays className="h-5 w-5" />
                    Planification
                  </NavLink>
                )}

                {(user?.role === "Admin" || user?.role === "Manager") && (
                  <NavLink
                    to="/data-explorer"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      cn(mobileLinkClass, isActive && "bg-accent")
                    }
                  >
                    <Table2 className="h-5 w-5" />
                    Data Explorer
                  </NavLink>
                )}

                {user?.role === "Admin" && (
                  <NavLink
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      cn(mobileLinkClass, isActive && "bg-accent")
                    }
                  >
                    <Shield className="h-5 w-5" />
                    Admin
                  </NavLink>
                )}
              </nav>

              {/* Profil utilisateur dans le menu mobile */}
              {user && (
                <div className="mt-8 pt-6 border-t">
                  <div className="flex items-center gap-3 px-4 py-3">
                    <User2 className="h-5 w-5" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{user.email}</span>
                      <span className="text-xs text-muted-foreground">{user.role}</span>
                    </div>
                  </div>
                </div>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
